import { Observable, ObservableInput, OperatorFunction } from "rxjs";
import { groupBy, mergeMap, switchMap } from "rxjs/operators";

/**
 * Like switchMap but the inner observables can be groupped and only the pending inner observable
 * that belongs to that specific group will be cancelled when a new item is emitted.
 *
 * ```ts
 * getAll$ = createEffect(
 * 		() => this.actions$.pipe(
 *  		map<string>(x => x.payload),
 *    			groupSwitchMap(group => this.service.getAll(group).pipe(
 *        		map(response => this.action.getAllSuccess(response))
 *        		catchError((error: Error) => of(this.action.getAllFail(error)))
 *    		)
 * 		);
 * 	)
 *
 * this.dispatch({ type: "GET_ALL", payload: "general" });
 * this.dispatch({ type: "GET_ALL", payload: "common" });
 * this.dispatch({ type: "GET_ALL", payload: "general" }); // this will cancel the previous general
 * ```
 *
 * @param project A function that, when applied to an item emitted by the source Observable, returns an
 * Observable.
 *
 * @param groupBySelector A function that extracts the key for grouping. The returned value can by anything, it will be used for grouping
 * using the === operator. By default, it is a stringified comparison.
 *
 * @return An Observable that emits the result of applying the
 * projection function to each item emitted by the source Observable.
 * Taking only the values from the most recently projected inner Observable
 * grouped by the given grouping function.
 */
export function groupSwitchMap<TInput, TResult>(
	project: (value: TInput, index?: number) => ObservableInput<TResult>,
	groupBySelector?: (value: TInput, index?: number) => any
): OperatorFunction<TInput, TResult> {
	return (source: Observable<TInput>) =>
		source.pipe(
			groupBy<any, TResult>(groupBySelector || defaultGroupBySelector),
			mergeMap((grouppedObservable) => grouppedObservable.pipe(switchMap(project)))
		);
}

function defaultGroupBySelector<T>(data: T): string {
	return JSON.stringify(data);
}
